// /src/components/RadioButton.jsx
import React from "react";
import { authFetch } from "../authFetch";
import API_BASE_URL from "../api";
import { getSelectedRadioShortcode } from "./RadioSwitcher";

const SHORTCODE_STORAGE_KEY = "selectedRadioShortcode";
const RADIO_ID_STORAGE_KEY = "selectedRadioId";

// Проверка логина по cookie (fallback)
function guessIsLoggedIn() {
  try { return document.cookie && document.cookie.includes("access_token"); } catch { return false; }
}

function persistSelectedRadio(shortcode, radio) {
  try {
    if (shortcode) localStorage.setItem(SHORTCODE_STORAGE_KEY, shortcode);
    else localStorage.removeItem(SHORTCODE_STORAGE_KEY);
  } catch {}

  try {
    const radioId = radio?.id ?? null;
    if (radioId != null) localStorage.setItem(RADIO_ID_STORAGE_KEY, String(radioId));
    else localStorage.removeItem(RADIO_ID_STORAGE_KEY);
  } catch {}
}

export default function RadioButton({ className, onChange, isLoggedIn }) {
  const [items, setItems] = React.useState([]);
  const [selected, setSelected] = React.useState(getSelectedRadioShortcode());
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const rootRef = React.useRef(null);
  const apiBase = React.useMemo(() => (API_BASE_URL || "").replace(/\/+$/, ""), []);
  const updateSelected = React.useCallback((next) => {
    setSelected((prev) => {
      const normalized = next ?? null;
      return prev === normalized ? prev : normalized;
    });
  }, []);

  // Фикс: сбрасывать выбранную станцию при разлогине
  React.useEffect(() => {
    const onLogout = () => {
      try { localStorage.removeItem(SHORTCODE_STORAGE_KEY); } catch {}
      try { localStorage.removeItem(RADIO_ID_STORAGE_KEY); } catch {}
      setSelected(null);
      setItems([]);
    };
    window.addEventListener('auth:unauthorized', onLogout);
    return () => window.removeEventListener('auth:unauthorized', onLogout);
  }, []);

  const logged = isLoggedIn ?? guessIsLoggedIn();

  const applyRadios = React.useCallback((rawItems) => {
    const radios = (Array.isArray(rawItems) ? rawItems : []).map((r) => ({
      id: r.id,
      name: r.name,
      shortcode: r.shortcode ?? null,
      status: String(r.status || ""),
      effective_status: String(r.effective_status || ""),
      channel_active: !!r.channel_active,
    }));

    const list = radios;
    setItems(list);

    const saved = getSelectedRadioShortcode();
    const withShortcode = list.filter((r) => !!r.shortcode);
    const hasSaved = saved && withShortcode.some((r) => r.shortcode === saved);

    let nextShortcode = null;
    let detailRadio = null;

    if (withShortcode.length === 0) {
      updateSelected(null);
      detailRadio = list[0] || null;
    } else if (hasSaved) {
      updateSelected(saved);
      nextShortcode = saved;
      detailRadio = withShortcode.find((r) => r.shortcode === saved) || null;
    } else {
      const first = withShortcode[0];
      const firstCode = first?.shortcode || null;
      updateSelected(firstCode);
      nextShortcode = firstCode;
      detailRadio = first || null;
    }

    persistSelectedRadio(nextShortcode, detailRadio);

    try {
      window.dispatchEvent(
        new CustomEvent("radio:change", {
          detail: {
            shortcode: nextShortcode,
            radioId: detailRadio?.id ?? null,
            radio: detailRadio,
          },
        })
      );
    } catch {}
    onChange?.(nextShortcode, detailRadio);
  }, [onChange, updateSelected]);

  // Основная загрузка радио и автоселект
  React.useEffect(() => {
    let alive = true;

    async function load() {
      if (!logged) {
        applyRadios([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const res = await authFetch(`${apiBase}/radios`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!alive) return;
        applyRadios(data);
      } catch (e) {
        if (!alive) return;
        setError(e.message || "Fetch error");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();

    const onRadiosData = (event) => {
      if (!alive || !logged) return;
      applyRadios(event?.detail?.items || []);
      setLoading(false);
      setError(null);
    };
    window.addEventListener("radios:data", onRadiosData);

    return () => {
      alive = false;
      window.removeEventListener("radios:data", onRadiosData);
    };
  }, [applyRadios, apiBase, logged]);

  // Для ручного выбора радио (клик)
  function handlePick(sc, itemsArg) {
    const list = itemsArg || items;
    const r = list.find(x => x.shortcode === sc) || null;
    updateSelected(sc);
    persistSelectedRadio(sc, r);
    try {
      window.dispatchEvent(new CustomEvent("radio:change", {
        detail: {
          shortcode: sc,
          radioId: r?.id ?? null,
          radio: r,
        },
      }));
    } catch {}
    onChange?.(sc, r);
    setOpen(false);
  }

  // Клик вне dropdown закрывает его
  React.useEffect(() => {
    const onOutside = (e) => {
      const el = rootRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside, { passive: true });
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  // Стили статусов (синхронизовано з бекендом: effective_status)
  const statusColor = (s) => {
    const k = String(s || "").toLowerCase();
    if (k === "connected") return "#5c5";                    // зелений
    if (k === "awaits_payment") return "#f0a500";             // очікує оплату
    if (k === "pending_payment_window") return "#f0a500";     // жовтий (тестове 12г вікно)
    if (k === "blocked_grace") return "#c55";                 // червоний (блок + таймер видалення)
    if (k === "awaits_connection") return "rgb(239,127,26)";   // очікує підключення
    return "#bbb";                                              // сірий для решти (inactive/unlinked)
  };

  // Видимость: если не залогинен или нет радио — не показывать
  const isVisible = logged && items.length > 0;
  if (!isVisible) return null;

  const isSingle = items.length === 1;
  const current = items.find(r => r.shortcode === selected) || items[0] || null;
  const label = current?.name || (loading ? "Радіо…" : "Обрати радіо");
  const eff =
    current?.status === "awaits_payment"
      ? "awaits_payment"
      : current?.effective_status || (current?.channel_active ? "connected" : "");
  const dotColor = statusColor(eff);

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        className={className}
        onClick={() => {
          if (!isSingle && items.length > 1) setOpen(v => !v);
        }}
        title={isSingle ? label : "Вибрати радіостанцію"}
        aria-expanded={open}
        disabled={isSingle}
        style={{
          position: "relative",
          background: "#fff",
          color: "#006e45",
          border: "2px solid #00723a",
          fontWeight: 700,
          fontSize: 17,
          borderRadius: 24,
          padding: "8px 20px 8px 18px",
          minWidth: 130,
          display: "flex",
          alignItems: "center",
          gap: 10,
          cursor: isSingle ? "default" : "pointer",
          boxShadow: "0 2px 8px #006e4520",
        }}
      >
        {label}
        {current && (
          <span
            style={{
              width: 9,
              height: 9,
              borderRadius: "50%",
              background: dotColor,
              display: "inline-block",
              margin: "0 7px 0 2px",
              border: `1px solid ${dotColor}`,
              verticalAlign: "middle",
            }}
          />
        )}
        {/* стрелка только если несколько радио */}
        {!isSingle && items.length > 1 && (
          <span aria-hidden style={{ fontSize: 16, opacity: 0.7 }}>▾</span>
        )}
      </button>

      {/* dropdown */}
      {open && items.length > 1 && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 12,
            boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
            minWidth: 240,
            zIndex: 1000,
            padding: 6,
          }}
        >
          {items.map((r) => {
            const eff =
              r?.status === "awaits_payment"
                ? "awaits_payment"
                : r.effective_status || (r.channel_active ? "connected" : "inactive");
            const color = statusColor(eff);

            return (
              <button
                key={r.id}
                role="menuitem"
                onClick={() => (r.shortcode ? handlePick(r.shortcode) : undefined)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid transparent",
                  background: r.shortcode === selected ? "#e8f0fe" : "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: r.shortcode ? "pointer" : "default",
                  opacity: r.shortcode ? 1 : 0.6,
                }}
              >
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    background: color,
                    border: `1px solid ${color}`,
                    marginRight: 7,
                    display: "inline-block",
                    verticalAlign: "middle",
                  }}
                />
                {r.name}
                {r.shortcode === selected && (
                  <span style={{ marginLeft: "auto", color: "#006e45" }}>✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}
      {error && (
        <div style={{ color: "#c00", fontSize: 13, marginTop: 5 }}>{error}</div>
      )}
    </div>
  );
}
